import { safeGetItem, safeSetItem, safeRemoveItem } from './storage.js';

export async function performInitDataHandshake() {
	if (!window.Telegram?.WebApp?.initData) {
		return null;
	}
	console.log("🔄 Performing zero-click initData handshake...");
	try {
		const res = await fetch("/auth/telegram/init-data", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ init_data: window.Telegram.WebApp.initData })
		});

		if (res.ok) {
			const json = await res.json();
			if (json.data && json.data.token) {
				const token = json.data.token;
				await safeSetItem("session_token", token);
				console.log("✅ initData authenticated successfully!");
				return token;
			}
		} else {
			console.warn("⚠️ initData validation returned:", res.status);
		}
	} catch (err) {
		console.error("❌ initData handshake error:", err);
	}
	return null;
}

let isRecoveringAuth = false;

export async function handleUnauthorized(nextPath) {
	if (isRecoveringAuth) return;
	isRecoveringAuth = true;

	try {
		console.warn("⚠️ Received 401 Unauthorized. Purging stale session token...");
		await safeRemoveItem("session_token");
		window.TelegramSession = null;

		// Attempt self-healing via initData if in Mini App
		if (window.Telegram?.WebApp?.initData) {
			console.log("🔄 Stale token evicted. Attempting recovery via initData...");
			const token = await performInitDataHandshake();
			if (token) {
				console.log("✅ Re-authenticated with initData! Reloading protected content...");
				isRecoveringAuth = false;
				window.TelegramSession = token;
				await loadProtectedContent();
				return;
			}
		}

		console.log("❌ No valid authentication available. Redirecting to login.");
		const dest = nextPath || (window.location.pathname + window.location.search);
		const hash = window.location.hash || "";
		let loginUrl = "/login?next=" + encodeURIComponent(dest);
		if (window.__SIRO_DESKTOP__ || document.cookie.includes("is_desktop=1")) {
			loginUrl += "&is_desktop=1";
		}
		window.location.href = loginUrl + hash;
	} finally {
		isRecoveringAuth = false;
	}
}

export async function checkMiniAppAutoLogin() {
	const path = window.location.pathname;
	if (path !== "/login" && path !== "/signup") {
		return;
	}
	if (window.Telegram?.WebApp?.initData) {
		console.log("🔄 Mini App detected on auth page. Attempting auto-login via initData...");
		const token = await performInitDataHandshake();
		if (token) {
			window.TelegramSession = token;
			const params = new URLSearchParams(window.location.search);
			const nextUrl = params.get("next") || "/app";
			window.location.href = nextUrl;
		}
	}
}

export async function loadProtectedContent() {
	const path = window.location.pathname;
	if (path === "/login" || path === "/signup" || path.startsWith("/static/") || path.startsWith("/dev-")) {
		return;
	}

	window.TelegramSession = null;

	let token = null;

	// Tier 1: In Telegram Mini App, initData is the authoritative, cryptographically verified identity.
	// Perform zero-click handshake immediately to obtain a fresh session token for this user.
	if (window.Telegram?.WebApp?.initData) {
		token = await performInitDataHandshake();
	}

	// Tier 2: Outside Telegram (or if initData handshake failed), check CloudStorage / localStorage
	if (!token) {
		token = await safeGetItem("session_token");
	}

	// Tier 3: If still no token, redirect to /login (preserving hash so initData isn't lost if in webview)
	if (!token) {
		console.log("❌ No session found. Redirecting to login.");
		const requestedPath = window.location.pathname + window.location.search;
		const hash = window.location.hash || "";
		let loginUrl = "/login?next=" + encodeURIComponent(requestedPath);
		if (window.__SIRO_DESKTOP__ || document.cookie.includes("is_desktop=1")) {
			loginUrl += "&is_desktop=1";
		}
		window.location.href = loginUrl + hash;
		return;
	}

	console.log("✅ Session Active!");
	window.TelegramSession = token;

	let fullPath = window.location.pathname + window.location.search;
	const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
	if (startParam && (window.location.pathname === "/" || window.location.pathname === "/app")) {
		if (startParam.startsWith("ws_")) {
			fullPath = "/ws/" + startParam.replace(/^ws_/, "");
		} else if (startParam.startsWith("task_")) {
			fullPath = "/tasks/" + startParam.replace(/^task_/, "");
		} else if (startParam === "my_tasks") {
			fullPath = "/my-tasks";
		}
	}

	htmx.ajax('GET', fullPath, {
		target: 'body',
		swap: 'innerHTML',
		headers: {
			'Authorization': 'Bearer ' + token
		}
	});
}


function showLoginError(form, errorDiv, errors) {
	if (errors && typeof errors === 'object') {
		const entries = Object.entries(errors);
		if (entries.length === 0) {
			errorDiv.textContent = "Login Failed: Unknown error";
			errorDiv.style.display = 'block';
			return;
		}
		const key = entries[0][0];
		const msg = entries[0][1];
		const field = form.querySelector('[name="' + key + '"]');
		if (field) {
			field.classList.add('border-destructive', 'ring-destructive/20');
			field.setAttribute('aria-invalid', 'true');

			let errMsg = document.createElement('p');
			errMsg.textContent = msg;
			errMsg.classList.add('text-destructive', 'text-sm', 'mt-1');
			field.after(errMsg);
		} else {
			errorDiv.textContent = msg;
			errorDiv.style.display = 'block';
		}
	} else {
		errorDiv.textContent = "Login Failed: " + (errors || "Unknown error");
		errorDiv.style.display = 'block';
	}
}

async function handleLoginSubmit(form) {
	const errorDiv = document.getElementById("error-msg");
	if (errorDiv) errorDiv.style.display = "none";

	form.querySelectorAll('input').forEach(i => {
		i.classList.remove('border-destructive', 'ring-destructive/20');
		i.removeAttribute('aria-invalid');
	});
	form.querySelectorAll('p.text-destructive').forEach(p => p.remove());

	const submitButtons = form.querySelectorAll('button:not([type="button"])');
	submitButtons.forEach(b => b.classList.add('htmx-request'));

	try {
		const formData = new FormData(form);
		const tgUser = window.Telegram?.WebApp?.initDataUnsafe?.user;
		if (tgUser && tgUser.id) {
			formData.set('chat_id', String(tgUser.id));
		}
		const response = await fetch(form.action || "/login", {
			method: 'POST',
			body: formData
		});

		const data = await response.json();

		if (!response.ok) {
			if (errorDiv) showLoginError(form, errorDiv, data.Error);
			return;
		}

		if (data.Data && data.Data.token) {
			const isStored = await safeSetItem("session_token", data.Data.token);
			if (!isStored && errorDiv) {
				errorDiv.textContent = "Storage Error: Failed to store session token";
				errorDiv.style.display = 'block';
				return;
			}
		}

		const nextUrl = form.getAttribute('data-next-url');
		window.location.href = nextUrl || "/app";

	} catch (err) {
		if (errorDiv) {
			errorDiv.textContent = "Login Failed: " + err.message;
			errorDiv.style.display = 'block';
		}
	} finally {
		submitButtons.forEach(b => b.classList.remove('htmx-request'));
	}
}

export function initLoginForm() {
	const form = document.getElementById('loginForm');
	if (!form) return;

	form.addEventListener('submit', function(e) {
		e.preventDefault();
		handleLoginSubmit(form);
	});
}

export function initSignupForm() {
	const form = document.getElementById('signupForm');
	if (!form) return;

	form.addEventListener('submit', function(e) {
		e.preventDefault();
		handleLoginSubmit(form);
	});
}